/* Thin wrapper over firebase/auth: lazily initializes the app from the
 * deployment's Firebase config and exposes the two login shapes the dashboard
 * offers. Firebase only proves WHO the user is — the resulting ID token is
 * exchanged with the identity service for the real session (see session.tsx). */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  applyActionCode,
  getAuth,
  getRedirectResult,
  getAdditionalUserInfo,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  GoogleAuthProvider,
  RecaptchaVerifier,
  deleteUser,
  type Auth,
  type User,
} from "firebase/auth";
import { CAPTCHA_ENABLED, firebaseConfig } from "./config";

/** Container id for the invisible reCAPTCHA widget (the sign-up screen renders
 *  a matching `<div id="captcha" />`). */
export const CAPTCHA_CONTAINER_ID = "captcha";

/** Carries a Firebase-style `code` so authErrors can map it to copy. */
export class CaptchaError extends Error {
  readonly code: string;
  constructor(code: "captcha/verification-failed" | "captcha/token-missing") {
    super(code);
    this.code = code;
    this.name = "CaptchaError";
  }
}

let app: FirebaseApp | undefined;

function auth(): Auth {
  // Lazy: the config is only present (and Firebase only needed) on gated
  // deployments; initializing at import time would break auth-off envs.
  app ??= initializeApp(firebaseConfig);
  return getAuth(app);
}

/* Invisible reCAPTCHA challenge on sign-up (mirrors studio's EXEC-2985). This
 * is a CLIENT-SIDE speed bump: solving Google's invisible challenge before
 * createUserWithEmailAndPassword stops trivial browser bots, but the token is
 * NOT itself sent or verified by this code. Real server-side abuse enforcement
 * is the shared Firebase project's reCAPTCHA Enterprise config — if that is in
 * ENFORCE mode the SDK attaches its own token to the auth request and the
 * backend rejects unverified calls. Needs no site key (Firebase manages it);
 * the solved token is cached for the page so the challenge isn't re-run on a
 * retried sign-up. */
let captchaVerifier: RecaptchaVerifier | undefined;
let captchaToken: string | null = null;

/** Render the invisible reCAPTCHA into `containerId`. No-op when captcha is
 *  disabled or already initialized; called from the sign-up screen on mount so
 *  the challenge is ready before submit. */
export function initSignupCaptcha(containerId = CAPTCHA_CONTAINER_ID): void {
  if (!CAPTCHA_ENABLED || captchaVerifier) return;

  captchaVerifier = new RecaptchaVerifier(auth(), containerId, {
    size: "invisible",
  });
}

/** Solve the invisible reCAPTCHA and cache its token. No-op when disabled.
 *  Throws a CaptchaError if the challenge fails or yields no token. */
async function ensureCaptchaToken(): Promise<void> {
  if (!CAPTCHA_ENABLED || captchaToken) return;

  const verifier =
    captchaVerifier ??
    new RecaptchaVerifier(auth(), CAPTCHA_CONTAINER_ID, { size: "invisible" });
  captchaVerifier = verifier;

  let token: string;
  try {
    token = await verifier.verify();
  } catch {
    throw new CaptchaError("captcha/verification-failed");
  }
  if (!token) throw new CaptchaError("captcha/token-missing");

  captchaToken = token;
}

/** Email/password login; resolves to the Firebase ID token. */
export async function firebaseEmailLogin(
  email: string,
  password: string,
): Promise<string> {
  const result = await signInWithEmailAndPassword(auth(), email, password);
  return result.user.getIdToken();
}

/** Email/password sign-up: creates the Firebase account and resolves to the
 *  new user's Firebase ID token (the user is signed in, in-memory). The
 *  identity exchange in session.tsx then provisions the org and triggers the
 *  tokenwise-branded verification email — the account starts unverified. */
export async function firebaseEmailSignup(
  email: string,
  password: string,
): Promise<string> {
  // Client-side reCAPTCHA challenge before creating the account (no-op unless
  // CAPTCHA_ENABLED; see the note above — not a server-enforced gate on its own).
  await ensureCaptchaToken();
  const result = await createUserWithEmailAndPassword(auth(), email, password);
  return result.user.getIdToken();
}

/** Delete the currently signed-in Firebase user. Used to roll back a just-
 *  created account when the identity exchange fails during sign-up, so the
 *  email stays reusable instead of leaving an orphan that "already exists" on
 *  the next attempt. Best-effort (callers ignore failures). */
export async function deleteCurrentFirebaseUser(): Promise<void> {
  const current = auth().currentUser;
  if (current) await deleteUser(current);
}

/** Send a Firebase password-reset email. Email/password runs on the shared
 *  studio Firebase project, whose action handler and reset template are
 *  project-wide, so the new-password page is Firebase-hosted — there is no
 *  in-app reset screen. The user sets the new password there and returns to
 *  sign in. (Verification, by contrast, is identity/SparkPost-branded.) */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth(), email);
  } catch (err) {
    // Anti-enumeration: an unregistered address resolves the same as a known
    // one so the screen shows the same "if an account exists…" confirmation
    // either way — don't rely solely on the project's enumeration-protection
    // toggle. Other errors (rate limit, network, bad email) still propagate.
    if ((err as { code?: string } | null)?.code === "auth/user-not-found") {
      return;
    }

    throw err;
  }
}

/** Apply an emailed verification oobCode (the /auth/action handler — see
 *  AuthActionRoute). Firebase marks the email verified server-side; no session
 *  is needed, so the link works in any browser. */
export async function applyEmailVerificationCode(
  oobCode: string,
): Promise<void> {
  await applyActionCode(auth(), oobCode);
}

/* Marks that THIS tab kicked off a signInWithRedirect, so the return load knows
 * to expect a redirect result and can recover it from currentUser if
 * getRedirectResult comes back empty (see consumeRedirectResult). sessionStorage
 * is per-tab and survives the full-page redirect round-trip; it does NOT leak
 * into ordinary reloads of a returning session, which keeps the cheap cookie
 * restore as the default bootstrap path. */
const REDIRECT_PENDING_KEY = "auth:redirectPending";

/** Google login via full-page redirect. Navigates the browser to Google and
 *  does not return in this page — the credential is picked up on the way back
 *  via consumeRedirectResult(). Resolves to void just before navigation; it
 *  rejects only if the redirect can't be started (e.g. misconfiguration).
 *  (GitHub was dropped; Google is the only social provider.) */
export async function firebaseSocialLogin(): Promise<void> {
  sessionStorage.setItem(REDIRECT_PENDING_KEY, "1");
  await signInWithRedirect(auth(), new GoogleAuthProvider());
}

/** A returning social-login redirect: the Firebase ID token to exchange, plus
 *  whether Google just created the account (`isNewUser` — distinguishes a
 *  social sign-up from a social login, which the URL alone can't tell us). */
export interface RedirectResult {
  token: string;
  isNewUser: boolean;
}

/** How close creation and last-sign-in must be for a sign-in to still count as
 *  part of the account's sign-up. Generous enough to cover a user re-attempting
 *  a failed identity exchange (a full Google round-trip), tight enough that a
 *  genuine returning login never lands inside it. */
const NEW_ACCOUNT_WINDOW_MS = 5 * 60 * 1000;

/** Whether this sign-in still belongs to the account's sign-up, derived from the
 *  account's own timestamps.
 *
 *  Needed because Firebase's `isNewUser` is true only on the round-trip that
 *  actually created the account, and two real paths lose it:
 *    - the identity exchange fails on the redirect return, so the user retries;
 *      Firebase created the account on the FIRST attempt, so the retry reports
 *      isNewUser=false and a genuine sign-up would be tracked as a login;
 *    - the getRedirectResult-already-consumed recovery branch below has no
 *      result to read it from at all.
 *
 *  Timestamps are RFC-1123 strings and either may be absent. Absent or
 *  unparseable ⇒ false: under-reporting a sign-up is safer than labelling a
 *  returning user's login as one — and this sits on the auth path, so it must
 *  never throw even if handed a partially-shaped user. */
function isFreshAccount(user: User): boolean {
  const creationTime = user.metadata?.creationTime;
  const lastSignInTime = user.metadata?.lastSignInTime;
  if (!creationTime || !lastSignInTime) return false;

  const created = Date.parse(creationTime);
  const signedIn = Date.parse(lastSignInTime);
  if (Number.isNaN(created) || Number.isNaN(signedIn)) return false;

  // Absolute difference: a sign-in stamped BEFORE creation means clock skew, not
  // a new account, and a bare subtraction would read that as freshly created.
  return Math.abs(signedIn - created) < NEW_ACCOUNT_WINDOW_MS;
}

/** Pick up a pending social-login redirect on the return page load: resolves to
 *  the redirect result when this load is the tail of a signInWithRedirect, or
 *  null otherwise (ordinary load / cookie-only restore). Called once during
 *  session bootstrap, before the cookie restore. */
export async function consumeRedirectResult(): Promise<RedirectResult | null> {
  const a = auth();
  const pending = sessionStorage.getItem(REDIRECT_PENDING_KEY) !== null;

  const result = await getRedirectResult(a);
  if (result) {
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
    const token = await result.user.getIdToken();
    // Firebase's flag is authoritative when set; fall back to the account's
    // timestamps so a sign-up retried after a failed identity exchange (account
    // already created on the first attempt) is still counted as a sign-up.
    return {
      token,
      isNewUser:
        !!getAdditionalUserInfo(result)?.isNewUser ||
        isFreshAccount(result.user),
    };
  }

  // getRedirectResult is single-use: it resolves the pending redirect exactly
  // once, then returns null. React 18 StrictMode double-invokes the bootstrap
  // effect in dev, so the SECOND run gets null here even though the redirect
  // just succeeded — and Firebase has already restored that user onto the auth
  // instance. When THIS tab initiated a redirect (the pending flag), recover the
  // session from currentUser so the second run still completes the identity
  // exchange instead of dropping it and landing back on /login. Gating on the
  // flag keeps ordinary reloads on the cheaper cookie restore. Firebase's
  // isNewUser isn't recoverable from currentUser, so newness is derived from the
  // account's own timestamps instead (see isFreshAccount).
  if (pending && a.currentUser) {
    sessionStorage.removeItem(REDIRECT_PENDING_KEY);
    const token = await a.currentUser.getIdToken();
    return { token, isNewUser: isFreshAccount(a.currentUser) };
  }

  /* This tab started a redirect and came back with neither a result nor a
   * restored user: the round trip failed. It is the ONLY place that failure is
   * observable — Firebase reports it as an ordinary "not signed in" load, so
   * without this the attempt disappears between AuthStarted and nothing. An
   * embedded-webview refusal (Google's `disallowed_useragent`) and an abandoned
   * consent screen both land here.
   *
   * Clearing the flag is the fix: it was previously only removed on the two
   * success paths, so a failed round trip left it set for the life of the tab —
   * which left the `pending && currentUser` recovery branch armed for an
   * ordinary later reload of that tab.
   *
   * The telemetry for this outcome is raised by the bootstrap in session.tsx,
   * which owns the auth attempt (see auth/authAttempt); this wrapper stays a
   * thin firebase/auth shim. */
  if (pending) sessionStorage.removeItem(REDIRECT_PENDING_KEY);

  return null;
}

/** The signed-in user's Firebase refresh token, or null if there is no Firebase user
 *  (a session restored from the identity cookie alone has none).
 *
 *  `refreshToken` is a public, documented property of the modular SDK's `User` — no
 *  internals, no cast. It is read for exactly one purpose: handing it to the `hopt` CLI
 *  after the user approves that on /cli-login. It grants everything the user can do, so
 *  nothing else in this app should call this, and it must never be logged or put in a URL.
 *
 *  Awaits `authStateReady()` first, which is not optional here: persistence is
 *  IndexedDB, so a fresh page load rehydrates `currentUser` asynchronously — and
 *  /cli-login is normally reached by exactly that route, since ForwardAfterAuth sends an
 *  explicit `redirect` through a full browser hop. Reading synchronously would
 *  intermittently see null and tell the user their sign-in failed. */
export async function currentRefreshToken(): Promise<string | null> {
  const instance = auth();
  await instance.authStateReady();

  return instance.currentUser?.refreshToken || null;
}

/** Clear the persisted Firebase user (IndexedDB) so sign-out doesn't leave a
 *  refresh token behind that could mint new ID tokens on a shared machine.
 *  auth() initializes the app if this page load never logged in (sessions
 *  restored from the cookie alone). */
export async function firebaseSignOut(): Promise<void> {
  await signOut(auth());
}
